/** @jsx isolateComponent */

import type { ReactNode } from 'react';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { Colors, Typography } from '@zapier/design-system';
import type { LazyloadOffset } from '@zapier/design-system';

import { ServiceIcon } from '../ServiceIcon';
import type { ServiceIconSize } from '../ServiceIcon';
import type { ServiceIconsService } from '../ServiceIcons';
import { ServiceIconGroup } from '../ServiceIconGroup';
import { ServiceIconShell } from '../ServiceIconShell';

type RenderServiceIconProps = Pick<Props, 'height' | 'lazyloadOffset'>;

type Props<Service = any> = {
  /** The height of the grouping, used to calculate the border radius. */
  height: ServiceIconSize;
  /**
   * Indicates whether the icons should lazily load. If `undefined`
   * then it'll default to `Img`'s `lazyloadOffset`.
   */
  lazyloadOffset?: LazyloadOffset;
  /**
   * Maxmium number of service icons to render. If more are provided, the final "icon" will be a number representing the number of unrendered services.
   */
  maxServices: number;
  /**
   * An array of service icons to render.
   *
   * - `name` is the name of the service whose icon is being displayed (used as alt text for the icon), e.g., "Google Sheets".
   * - `src` is a URL for the service icon image to be rendered. If this is an Imgix URL, a large-format image can be used and the image will automatically be resized.
   */
  services: Array<Service>;
  /**
   * Function to render the service icon
   */
  renderServiceIcon?: (
    service: Service,
    index: number,
    props: RenderServiceIconProps
  ) => ReactNode;
};

const Styles = labelStyles('ZapServiceIcons', {
  root: {
    display: 'flex',
    userSelect: 'none',
  },
  number: {
    ...Typography.bodyText900,
    color: Colors.black,
    fontWeight: 700,
    textIndent: -2, // Optical alignment
  },
});

const defaultRenderServiceIcon = (
  service: ServiceIconsService,
  index: number,
  props: RenderServiceIconProps
) => {
  return (
    <ServiceIcon
      isInGroup={true}
      key={index}
      serviceName={service.name}
      size={props.height}
      src={service.src}
    />
  );
};

/**
 * Renders icons representing the services (aka apps, aka integrations) within
 * a Zap.
 *
 * The aesthetics of this component are intended to convey that the services
 * part of the same Zap or Zap template. For rendering a set of loosely
 * associated services, use [`ServiceIcons`](/#/Patterns/ServiceIcons).
 */
export const ZapServiceIcons = <Service,>(props: Props<Service>) => {
  const {
    height,
    maxServices,
    services,
    renderServiceIcon = defaultRenderServiceIcon,
  } = props;

  // Capped at 99 so that the number of extra services doesn't break the layout.
  // +99 services is a lot of services!
  const extraServicesCount = Math.min(services.length - maxServices, 98);
  const hasExtraServices = extraServicesCount > 0;

  // Subtract one from `maxServices` to leave one empty space to render the
  // extra service count (e.g. +2) into.
  const servicesToShow = hasExtraServices ? maxServices - 1 : maxServices;

  // Using `index` as `key` since the same `service` could potentially appear
  // multiple times (such as in a Zap which uses the same service in multiple
  // stpes) and therefore would not be unique.
  const icons = services
    .slice(0, servicesToShow)
    .map((service: Service, index: number) => {
      return renderServiceIcon(service as any, index, {
        height,
      });
    });

  const numericPlaceholderLabel = `Plus ${extraServicesCount} more apps.`;
  const numericPlaceholderWidth = extraServicesCount > 9 ? height + 5 : height;

  if (hasExtraServices) {
    icons.push(
      <ServiceIconShell
        height={height}
        isInGroup={true}
        key="numeric-placeholder"
        width={numericPlaceholderWidth}
      >
        <span aria-label={numericPlaceholderLabel} css={Styles.number}>
          <span aria-hidden="true">+{extraServicesCount + 1}</span>
        </span>
      </ServiceIconShell>
    );
  }

  return <ServiceIconGroup height={height}>{icons}</ServiceIconGroup>;
};

ZapServiceIcons.defaultProps = {
  maxServices: 6,
};
